import { FC, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Ribbon, Tooltip } from '@codex/basics';
import { Close } from '@codex/organisms';


type GridItemRowLocationEnum = 'left' | 'middle' | 'right'

const TooltipWrapper = styled.div<{
  monthlyPricePaymentMessage: string,
  specialCreditOfferMessage: string,
  gridItemRowLocation: GridItemRowLocationEnum | undefined
}>`
  position: absolute;
  font-size: 14px;
  z-index: 3;
  background-color: #f5f6fc;
  cursor: default;

  // styles for desktop on grid and list
  @media ${({ theme }) => theme.breakpoints.desktop} {
    right: ${({ monthlyPricePaymentMessage }) => (monthlyPricePaymentMessage ? 'calc(100% - 10px)' : 'calc(100% + 15px)')};
    top: ${({ monthlyPricePaymentMessage }) => (monthlyPricePaymentMessage ? '-115px' : '-125px')};

    // when specialCreditOfferMessage is displayed
    ${({ specialCreditOfferMessage, monthlyPricePaymentMessage }) => specialCreditOfferMessage && css`
      top: ${monthlyPricePaymentMessage ? '-130px' : '-135px'};
    `}
  }

  // styles for wide desktop on grid and list
  @media ${({ theme }) => theme.breakpoints.wide} {
    right: ${({ monthlyPricePaymentMessage }) => (monthlyPricePaymentMessage ? 'calc(100% - 35px)' : 'calc(100% + 15px)')};
    top: ${({ monthlyPricePaymentMessage }) => (monthlyPricePaymentMessage ? '-115px' : '-125px')};

    // when specialCreditOfferMessage is displayed
    ${({ specialCreditOfferMessage, monthlyPricePaymentMessage }) => specialCreditOfferMessage && css`
      top: ${monthlyPricePaymentMessage ? '-130px' : '-135px'};
    `}
  }

  // styles for list on tablet 
  ${(
    { gridItemRowLocation, monthlyPricePaymentMessage, specialCreditOfferMessage }
  ) => typeof gridItemRowLocation === 'undefined' && css`
    @media ${({ theme }) => theme.breakpoints.tablet} {
      right: calc(100% + 15px);
      top: -120px;

      // when monthlyPricePaymentMessage is displayed
      ${monthlyPricePaymentMessage && css`
        right: calc(100% - 28px);
        top: -115px;
      `};

      // when specialCreditOfferMessage is displayed
      ${specialCreditOfferMessage && css`
        top: ${monthlyPricePaymentMessage ? '-130px' : '-135px'};
      `};
    }
  `}

  // styles for grid on tablet  
  ${({ gridItemRowLocation }) => typeof gridItemRowLocation !== 'undefined' && css`
    @media ${({ theme }) => theme.breakpoints.tablet} {
      margin-top: 10px;

      ${gridItemRowLocation === 'middle' && css`
        right: -40px;
      `};

      ${gridItemRowLocation === 'left' && css`
        right: unset;
      `};

      ${gridItemRowLocation === 'right' && css`
        right: -5px;
      `};
    }
  `};
`;

const StyledTooltip = styled(Tooltip)`
  position: relative;
  width: 24em;
  padding: 10px 0 15px;
  border-radius: 0;
  background-color: #f5f6fc;
  &:focus {
    // removes blue border from Chrome
    outline: none;
  }

  // styles for arrows in grid on tablet 
  ${({ gridItemRowLocation }) => typeof gridItemRowLocation !== 'undefined' && css`
    @media ${({ theme }) => theme.breakpoints.tablet} {
      ${gridItemRowLocation === 'left' && css`
        &::before {
          left: 85px;
        }
      `};

      ${gridItemRowLocation === 'right' && css`
        &::before {
          left: 195px;
        }
      `};
    }
  `};
`;

const StyledClose = styled(Close)`
  position: absolute;
  top: 5px;
  right: 0;
`;

const TopLogo = styled.img`
  width: 180px;
  position: relative;
  right: 14px;
  bottom: 3px;
`;

const MonthlyPricePaymentMessage = styled.div`
  margin-top: 5px;
  margin-bottom: 8px;
  padding: 0 20px;
`;

const TopCreditMessage = styled.div`
  margin-bottom: 8px;
  padding: 0 20px;
`;

const BottomCreditMessage = styled.div`
  margin-bottom: 8px;
  padding: 0 20px;
  a {
    color: ${({ theme }) => theme.colors.link.default};
    :hover {
      ${({ theme }) => theme.colors.link.hover};
    }
  }
`;

const FindOutMoreCreditLink = styled.div`
  padding: 0 20px;
  a {
    color: ${({ theme }) => theme.colors.link.default};
    :hover {
      ${({ theme }) => theme.colors.link.hover};
    }
  }
`;

const RibbonWrapper = styled.div`
  position: relative;
  height: 20px;
  margin-top: 10px;
`;

const StyledRibbon = styled(Ribbon)`
  top: 0;
  width: calc(100% + 7px);
  padding: 2px 0 2px 15px;
  text-align: left;
  background-color: #723f89;
`;

type DeviceTypeEnum = 'wide' | 'desktop' | 'tablet' | 'mobile' | 'SSR';

type Props = {
  onCloseButtonClick?(): void,
  onOutsidePopupClick?(): void,
  topLogoUrl: string,
  monthlyPricePaymentMessage: string,
  topCreditMessage: string,
  bottomCreditMessage: string,
  findOutMoreCreditLink: string,
  specialCreditOfferMessage: string
  gridItemRowLocation: GridItemRowLocationEnum | undefined,
  deviceType?: DeviceTypeEnum
}

const ProductCreditTooltip: FC<Props> = ({ deviceType, monthlyPricePaymentMessage, gridItemRowLocation,
  onCloseButtonClick = () => {}, onOutsidePopupClick = () => {}, topLogoUrl,
  topCreditMessage, bottomCreditMessage, findOutMoreCreditLink, specialCreditOfferMessage }) => {
  
  const tooltipRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onOutsidePopupClick();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [tooltipRef]);
  
  return (
    <TooltipWrapper
      onClick={(e) => {
        const clickedElement = e.target as HTMLElement;
        // because the content of Tooltip can contain links which are added using dangerouslySetInnerHTML
        // we have to detect when user clicks on that link and don't prevent default behaviour in order to allow
        // redirecting to the link
        if (clickedElement.tagName !== 'A') {
          e.preventDefault();
        }
      }}
      monthlyPricePaymentMessage={monthlyPricePaymentMessage}
      specialCreditOfferMessage={specialCreditOfferMessage}
      gridItemRowLocation={gridItemRowLocation}
      ref={tooltipRef}
    >

      <StyledTooltip
        arrow={(deviceType === 'tablet' && typeof gridItemRowLocation !== 'undefined') ? 'top' : 'right'}
        monthlyPricePaymentMessage={monthlyPricePaymentMessage}
        gridItemRowLocation={gridItemRowLocation}
      >
        <StyledClose
          onClose={(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            onCloseButtonClick();
          }}
        />
        <TopLogo
          src={topLogoUrl}
          alt={'top logo'}
        />
        {!!monthlyPricePaymentMessage && (
          <MonthlyPricePaymentMessage
            dangerouslySetInnerHTML={{ __html: monthlyPricePaymentMessage }}
          />
        )}
        <TopCreditMessage
          dangerouslySetInnerHTML={{ __html: topCreditMessage }}
        />
        <BottomCreditMessage
          dangerouslySetInnerHTML={{ __html: bottomCreditMessage }}
        />
        <FindOutMoreCreditLink
          dangerouslySetInnerHTML={{ __html: findOutMoreCreditLink }}
        />
        {!!specialCreditOfferMessage && (
          <RibbonWrapper>
            <StyledRibbon side={'left'} type={'info'}>
              <span dangerouslySetInnerHTML={{ __html: specialCreditOfferMessage }} />
            </StyledRibbon>
          </RibbonWrapper>
        )}
      </StyledTooltip>
    </TooltipWrapper>
  );
};

ProductCreditTooltip.displayName = 'ProductCreditTooltip';

export default ProductCreditTooltip;
